import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import createComponent from 'styles/fela/createComponent';
import { Box } from 'components';

const StyledRadioButton = createComponent(({ theme, color, checked, focused }) => ({
  border: `2px solid ${theme.color.secondary}`,
  borderRadius: '50%',
  cursor: 'pointer',
  height: theme.margin.large,
  width: theme.margin.large,
  position: 'relative',
  flex: '0 0 auto',
  backgroundColor: theme.color.whiteBackground,

  transition: 'background-color 100ms linear, border-color 100ms linear, box-shadow 100ms linear',
  extend: [
    {
      condition: checked,
      style: {
        '::before': {
          backgroundColor: theme.color[color],
          borderRadius: '50%',
          bottom: '3px',
          content: '""',
          display: 'block',
          left: '3px',
          position: 'absolute',
          right: '3px',
          top: '3px',
        },
      },
    },
    {
      condition: focused,
      style: {
        outline: `2px solid ${theme.color.primary}`,
        outlineOffset: '2px',
      },
    },
  ],
}));

const HiddenInput = createComponent(() => ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
}), 'input');

const LabelWrapper = createComponent(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
}), 'label');

const RadioButton = ({ label, color = 'inputColor', checked, onFocus: onFocusProp = null, onBlur: onBlurProp = null, onKeyDown: onKeyDownProp = null, ...otherProps }) => {
  const radioButtonRef = useRef(null);
  const [focused, setFocused] = useState(false);

  const onFocus = (e) => {
    setFocused(true);
    if (onFocusProp) {
      onFocusProp(e);
    }
  };

  const onBlur = (e) => {
    setFocused(false);
    if (onBlurProp) {
      onBlurProp(e);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      radioButtonRef.current.click();
    }
    if (onKeyDownProp) {
      onKeyDownProp(e);
    }
  };

  return (
    <LabelWrapper>
      <HiddenInput
        innerRef={radioButtonRef}
        type="radio"
        checked={checked}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        {...otherProps}
      />
      <StyledRadioButton
        color={color}
        checked={checked}
        focused={focused}
      />
      <Box
        display="inline-block"
        verticalAlign="middle"
        pl="small"
      >
        {label}
      </Box>
    </LabelWrapper>
  );
};

RadioButton.propTypes = {
  label: PropTypes.node.isRequired,
  color: PropTypes.string,
  checked: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
};

export default RadioButton;
